import { Button } from "@/components/ui/button";
import { ArrowRight, Home, Shield, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import CanonicalTag from "@/components/CanonicalTag";

const ColonialADUDesignSanMateo = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <CanonicalTag url="https://udaadu.com/colonial-adu-design-san-mateo" />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-6">
            Colonial ADU Design in San Mateo
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Honor traditional American architecture in San Mateo with colonial ADU designs that feature classic proportions, elegant details, and enduring charm.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Your Colonial ADU Quote
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg">
              View Colonial Portfolio
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Colonial ADU Design in San Mateo?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-lg bg-card border">
              <Home className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Classic Elegance</h3>
              <p className="text-muted-foreground">
                Refined colonial architecture that adds distinguished character to San Mateo's diverse residential neighborhoods.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Proven Design</h3>
              <p className="text-muted-foreground">
                Time-tested architectural principles that ensure lasting beauty and functionality in San Mateo's climate.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Crown className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Prestigious Appeal</h3>
              <p className="text-muted-foreground">
                Colonial styling that enhances property values and creates rental units with broad market appeal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-3xl font-bold mb-6">Colonial ADU Design Excellence in San Mateo</h2>
          
          <p className="text-lg text-muted-foreground mb-6">
            San Mateo's established neighborhoods and appreciation for traditional architecture make it an ideal location for colonial-style ADUs. Our colonial designs respect historical precedents while incorporating modern conveniences and energy efficiency standards that today's residents expect.
          </p>

          <h3 className="text-2xl font-semibold mb-4">Colonial Design Features</h3>
          <ul className="space-y-2 text-muted-foreground mb-6">
            <li>• Symmetrical facades with centered entrances</li>
            <li>• Multi-pane windows with decorative shutters</li>
            <li>• Classic dormers and traditional rooflines</li>
            <li>• Column-supported porches and entryways</li>
            <li>• Formal interior layouts with defined rooms</li>
            <li>• Traditional materials and color schemes</li>
          </ul>

          <h3 className="text-2xl font-semibold mb-4">San Mateo Colonial ADU Advantages</h3>
          <p className="text-muted-foreground mb-6">
            Colonial ADUs in San Mateo provide excellent long-term value due to their timeless appeal and broad market acceptance. These designs particularly resonate with families and professionals who appreciate traditional American architecture and the sense of permanence it conveys in San Mateo's dynamic Peninsula location.
          </p>

          <div className="bg-muted/20 p-6 rounded-lg mb-8">
            <h4 className="text-xl font-semibold mb-3">Ready to Build Your Colonial ADU in San Mateo?</h4>
            <p className="text-muted-foreground mb-4">
              Our team specializes in authentic colonial design while ensuring your ADU meets all modern building codes and efficiency standards.
            </p>
            <Button className="group">
              Start Your Colonial ADU Project
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Add Colonial Elegance to Your San Mateo Property</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Experience the enduring beauty and prestige of colonial architecture in your custom San Mateo ADU.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Free Colonial ADU Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">Contact Our Design Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ColonialADUDesignSanMateo;